// @ts-nocheck
/* eslint-disable spire/avoid-dynamic-translate */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getProductCollectionV2 } from "@insite/client-framework/Services/ProductServiceV2";
import translate from "@insite/client-framework/Translate";
import { ProductCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { GridItem, Select, Typography } from "@insite/mobius/index";
import useInfinteScroll from "@store/Framework/Common/Hooks/useInfiniteScroll";
import useLocalizeCurrency from "@store/Framework/Common/Hooks/useLocalizeCurrency";
import useReportApi from "@store/Framework/Common/Hooks/useReportAPI/useReportAPI";

import { createSheetFromProductData } from "@store/Framework/Common/Utilities/excelExportFunctions";
import {
    ExpenditureByProductItemModel,
    ExpenditureByProductModel,
    ExpendituresByManufacturersModel,
} from "@store/Framework/Services/Reporting/ReportingModels";
import { isExpenditureByProductModel } from "@store/Framework/Services/Reporting/ReportingTypeGuards";
import { reportParametersSelector } from "@store/Framework/Store/Bunzl/Pages/Reporting/ReportOrderFrequency/ReportOrderFrequencySelectors";
import useClear from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useClear";
import { CustomWidgetModule, ProductData, ProductDataLineItems, Products } from "@store/Framework/Types/types";
import SearchBox from "@store/Library/Components/Bunzl/Common/SearchBox";
import { DataTableData, FormatDictionary } from "@store/Library/Components/Reporting/ApiDataTable/ReportDataTable";
import AccordionLayout from "@store/Library/Components/Reporting/Common/AccordionLayout";
import ApiButtons from "@store/Library/Components/Reporting/Common/ApiButtons";
import ButtonExportFile from "@store/Library/Components/Reporting/Common/ButtonExportFile";
import ProductSection, { ProductSectionStyles } from "@store/Library/Components/Reporting/Common/ProductSection";
import ReportContainer from "@store/Library/Components/Reporting/Common/ReportContainer";
import ReportingFilterGridItem from "@store/Library/Components/Reporting/Common/ReportingFilterGridItem";
import { LocationPicker } from "@store/Library/Components/Reporting/LocationPicker/LocationPicker";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { getMediaQuery } from "@toolkit/Framework/Common/Utilities/mediaQueries";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useWebsite } from "@toolkit/Framework/Store/Context/ContextHooks";
import chunk from "lodash/chunk";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import styled, { css } from "styled-components";

const DatePicker = getWidgetComponent("Reports/ReportSearchFieldDateRange");
const mobileQuery = getMediaQuery({ option: "only", first: true, min: 0, max: 700, last: false });
export const productionOverrideStyles: ProductSectionStyles = {
    productSectionContainer: {
        css: css`
            width: auto;
            margin-block: 2rem;
        `,
    },
};
export const ProductSectionContainer = styled(GridItem)`
    row-gap: 2rem;
    width: 100%;
    justify-content: center;
    align-items: flex-end;
    flex-direction: column;
`;

export interface GroupByLocationData {
    location: string;
    items: Products;
}
type Grouping = "Consolidated" | "Location";
const headers = ["Invoice #", "Order #", "Quantity", "UM", "Expenditure"];
const dataKeys: (keyof ProductDataLineItems)[] = ["invoiceNumber", "orderNumber", "quantity", "UAM", "totcost"];
const filterProduct = (searchTerm: string, products: Products) => {
    if (!searchTerm) {
        return products;
    }
    const filteredProducts: Products = {};
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    Object.keys(products).forEach(itemName => {
        const product = products[itemName];
        if (
            product.itemNumber?.toLowerCase().includes(lowerCaseSearchTerm) ||
            itemName.toLowerCase().includes(lowerCaseSearchTerm)
        ) {
            filteredProducts[itemName] = product;
        }
    });
    return filteredProducts;
};

export const getFixedProducts = async <T extends ExpenditureByProductModel | ExpendituresByManufacturersModel>(
    data: T,
    productPrefix: string,
): Promise<T> => {
    const { items } = data;
    const uniqueSkus = [...new Set(items?.map(item => item.itemnumber))];
    const fixedUniqueSkus = uniqueSkus.map(sku => `${productPrefix}${sku}`);
    const chunkedSkus = chunk(fixedUniqueSkus, 32);
    const productsPromises = chunkedSkus.map(chunk =>
        getProductCollectionV2({
            productNumbers: chunk,
        }),
    );
    const productsReturnList = await Promise.all(productsPromises);
    const productsReturn = productsReturnList.reduce(
        (acc, products) => {
            acc.products = acc.products.concat(products?.products || []);
            return acc;
        },
        { products: [] as ProductModel[] },
    );

    const dataWithFixedNames = items.map(item => {
        const product = productsReturn.products?.find(
            product => product.productNumber === `${productPrefix}${item.itemnumber}`,
        );

        return {
            ...item,
            itemdesc: product?.productTitle ?? item.itemdesc,
        };
    });
    return { ...data, items: dataWithFixedNames };
};

const getProducts = (data: ExpenditureByProductItemModel[]) => {
    const products: Products = {};

    data.forEach(item => {
        if (!products[item.itemdesc]) {
            products[item.itemdesc] = {
                itemNumber: item.itemnumber,
                totCost: item.totcost,
                totQuantity: item.totquantity,
                data: [],
            };
        } else {
            products[item.itemdesc].totCost += item.totcost;
            products[item.itemdesc].totQuantity += item.totquantity;
        }
        products[item.itemdesc].data.push({
            invoiceNumber: item.invoicenum,
            orderNumber: item.orderId,
            quantity: item.totquantity,
            UAM: item.unitOfMeasure,
            totcost: item.totcost,
        });
    });
    return products;
};
const createLocationExcelData = (groupByLocationData: GroupByLocationData[]) => {
    const seen = {} as { [key: string]: number };
    return ([] as unknown as DataTableData<any>[]).concat(
        ...groupByLocationData.map(location => {
            let locationName = location.location.slice(0, 20);
            if (seen[locationName]) {
                seen[locationName]++;
                locationName += `-${seen[locationName]}`;
            } else {
                seen[locationName] = 1;
            }

            const sheetData = createSheetFromProductData(
                location.items,
                headers,
                dataKeys,
                translate("Expenditure by Product Report"),
                `${locationName} `,
            );
            const sheetDataWithLocation = sheetData.map(sheet => {
                return {
                    ...sheet,
                    data: sheet.data.map(data => ({ ...data, location: location.location })),
                    headers: ["Location", ...sheet.headers],
                    dataKeys: ["location", ...sheet.dataKeys],
                };
            });
            return sheetDataWithLocation;
        }),
    );
};
const ExpenditureByProductReport = () => {
    const onClear = useClear();
    const handleClear = () => {
        onClear();
        setHasSubmitted(false);
        setGroupByConsolidatedData(undefined);
        setGroupByLocationData([]);
        setPage(1);
        setStartInfiniteScroll(false);
    };
    const [startInfiniteScroll, setStartInfiniteScroll] = React.useState(false);

    const { itemsToShow, setPage } = useInfinteScroll(10, startInfiniteScroll);
    const [searchTerm, setSearchTerm] = useState("");
    const reportParameters = useSelector(reportParametersSelector);
    const [grouping, setGrouping] = useState<Grouping>("Consolidated");
    const [groupByLocationData, setGroupByLocationData] = React.useState<GroupByLocationData[]>([]);
    const [groupByConsolidatedData, setGroupByConsolidatedData] = React.useState<Products | null>();
    const { isError, updateData, isLoading, hasSubmitted, setHasSubmitted, setLoading } =
        useReportApi("expenditureByProduct");
    const [locationExcelData, setLocationExcelData] = useState<DataTableData<any>[]>();
    const [consolidatedExcelData, setConsolidatedExcelData] = useState<DataTableData<any>[]>();
    const localizeCurrency = useLocalizeCurrency();
    const productPrefix = `${useWebsite().name.toUpperCase()}-`;
    const handleSearch = (searchTerm: string) => {
        setSearchTerm(searchTerm);
        setPage(1);
    };
    const handleDataUpdate = async () => {
        setPage(1);
        const apiReturn = await updateData({
            ...reportParameters,

            grouping: "location",
        });
        if (!apiReturn) {
            setGroupByConsolidatedData(null);
        }
        if (isExpenditureByProductModel(apiReturn)) {
            setLoading(true);
            const fixedApiReturn = await getFixedProducts<ExpenditureByProductModel>(apiReturn, productPrefix);
            setLoading(false);
            const groupByCondolidatedData = getProducts(fixedApiReturn.items);
            setGroupByConsolidatedData(groupByCondolidatedData);

            const locations = [...new Set(fixedApiReturn.items.map(item => item.workinggrouping))];
            const groupByLocationData = locations.map(location => {
                const locationItems = fixedApiReturn.items.filter(item => item.workinggrouping === location);
                const items = getProducts(locationItems);
                return {
                    location,
                    items,
                };
            });
            setGroupByLocationData(groupByLocationData);
            setStartInfiniteScroll(true);

            const consolidatedExcelData = createSheetFromProductData(
                groupByCondolidatedData,
                headers,
                dataKeys,
                translate("Expenditure by Product Report"),
                "",
            );

            const locationExcelData = createLocationExcelData(groupByLocationData);

            setConsolidatedExcelData(consolidatedExcelData);
            setLocationExcelData(locationExcelData);
        }
    };
    const filteredProducts = useMemo(() => {
        if (grouping === "Consolidated") {
            return filterProduct(searchTerm, groupByConsolidatedData || {});
        }
        return groupByConsolidatedData;
    }, [groupByConsolidatedData, searchTerm, grouping, groupByLocationData]);

    const filteredLocations = useMemo(() => {
        if (grouping === "Location") {
            const filteredLocations = groupByLocationData.map(location => {
                const filteredItems = filterProduct(searchTerm, location.items);
                if (Object.keys(filteredItems).length === 0) {
                    return undefined;
                }
                return {
                    ...location,
                    items: filteredItems,
                };
            });
            return filteredLocations.filter(location => location !== undefined) as GroupByLocationData[];
        }
        return groupByLocationData;
    }, [groupByLocationData, searchTerm, grouping]);

    let elementsShown = 0;
    const formatDictionary: FormatDictionary = {
        totcost: localizeCurrency,
    };

    return (
        <ReportContainer>
            <AccordionLayout
                extendedStyles={{
                    accordionSection: {
                        mergeCss: true,
                        panelProps: {
                            css: css`
                                margin-bottom: 0 !important;
                                padding-bottom: 1.5rem !important;
                            `,
                        },
                    },
                    accordion: {
                        mergeCss: true,
                        css: css`
                            margin-bottom: 0;
                        `,
                    },
                }}
            >
                <ReportingFilterGridItem doubleSized={true}>
                    <DatePicker />
                </ReportingFilterGridItem>
                <ReportingFilterGridItem>
                    <LocationPicker isError={isError} />
                </ReportingFilterGridItem>

                <ApiButtons onClear={handleClear} isLoading={isLoading} updateData={handleDataUpdate} />
            </AccordionLayout>
            <StyledWrapper
                css={css`
                    ${mobileQuery} {
                        padding-inline: 1rem;
                    }
                    width: 100%;
                    display: flex;
                    flex-wrap: wrap;
                    row-gap: 2rem;
                    margin-bottom: ${grouping === "Consolidated" ? "3rem" : "2rem"};
                    justify-content: space-between;
                `}
            >
                <StyledWrapper
                    css={css`
                        display: flex;
                        align-items: flex-end;
                        column-gap: 1.5rem;
                        width: 100%;
                        max-width: ${pxToRem(660)};
                    `}
                >
                    <SearchBox
                        placeholder={translate("search")}
                        title=""
                        extendedStyles={{
                            gridItem: {
                                css: css`
                                    padding-block: 0;
                                `,
                            },
                        }}
                        disabled={!startInfiniteScroll}
                        onSearch={handleSearch}
                    />
                    <Select
                        value={grouping}
                        onChange={event => setGrouping(event.currentTarget.value as Grouping)}
                        label={<Typography as="p">{translate("Grouping")}</Typography>}
                    >
                        <option disabled value="">
                            {translate("Select Grouping")}
                        </option>
                        <option key={1} value="Consolidated">
                            {translate("Consolidated")}
                        </option>
                        <option key={2} value="Location">
                            {translate("Location")}
                        </option>
                    </Select>
                </StyledWrapper>
                <StyledWrapper
                    css={css`
                        align-self: flex-end;

                        & button {
                            margin-left: 0rem !important;
                        }
                    `}
                >
                    <ButtonExportFile
                        excelData={grouping === "Consolidated" ? consolidatedExcelData : locationExcelData}
                        disabled={!groupByLocationData.length}
                    />
                </StyledWrapper>
            </StyledWrapper>
            <>
                {grouping === "Consolidated"
                    ? filteredProducts &&
                      Object.keys(filteredProducts).map((key: string, index) => {
                          const product: ProductData = filteredProducts[key];
                          if (index > itemsToShow) {
                              return null;
                          }
                          return (
                              <ProductSection
                                  headers={headers}
                                  dataKeys={dataKeys}
                                  key={key}
                                  itemName={key}
                                  productData={product}
                                  formatDictionary={formatDictionary}
                                  extendedStyles={productionOverrideStyles}
                              />
                          );
                      })
                    : filteredLocations.map((locationData: GroupByLocationData) => {
                          return (
                              <>
                                  <Typography
                                      css={css`
                                          width: "100%";
                                          margin-block: 1rem;
                                          padding: 1rem;
                                          text-align: center;
                                      `}
                                      variant="h2"
                                  >
                                      {locationData.items && locationData.location}
                                  </Typography>

                                  {Object.keys(locationData.items).map((key: string) => {
                                      const product: ProductData = locationData.items[key];
                                      if (elementsShown > itemsToShow) {
                                          return null;
                                      }

                                      elementsShown++;
                                      return (
                                          <ProductSection
                                              headers={headers}
                                              dataKeys={dataKeys}
                                              key={key}
                                              formatDictionary={formatDictionary}
                                              itemName={key}
                                              extendedStyles={productionOverrideStyles}
                                              productData={product}
                                          />
                                      );
                                  })}
                              </>
                          );
                      })}
            </>
            {hasSubmitted && !isLoading && groupByConsolidatedData === null && !isError && (
                <Typography as="p" variant="h3">
                    {translate("No results found")}
                </Typography>
            )}
        </ReportContainer>
    );
};

const widgetModule: CustomWidgetModule = {
    component: ExpenditureByProductReport,
    definition: {
        group: "Reporting",
        displayName: "Expenditure By Product Report",
    },
};

export default widgetModule;
